<script setup lang="ts">
import {
    Button,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, nextTick, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { api, type Me, type WorkingHours } from '@/api'
import ConnectYourMailPanel from '@/components/ConnectYourMailPanel.vue'
import MyMailTokensPanel from '@/components/MyMailTokensPanel.vue'
import SignInWaysPanel from '@/components/SignInWaysPanel.vue'
import { loadUiMode, saveUiMode, useUiMode } from '@/composables/useUiMode'
import { scopeChips } from '@/lib/scopeChips'
import { availableTimezones } from '@/lib/timezone'

/**
 * #630: what the signed-in person holds in this enterprise, and who brought them in — so a narrow grant reads
 * as deliberate rather than broken, and they know whom to ask for more.
 *
 * Built from the session identity payload (`GET /auth/me`) alone: it already carries this person's own
 * ownership flag and scopes, and names their inviter as an identity only. It never reads the people-and-grants
 * listing, which is owner-only and is about everybody else, so this screen can be opened holding no scope at
 * all (it is one of the deliberately unscoped destinations in `navGrant.ts`) and says nothing about anybody
 * else's grant. Read-only about the grant: what somebody holds is changed by an owner on the People screen, never
 * here. The one thing a person changes here is their own Standard/Advanced mode (#1949), which is about how the portal
 * reads to them and grants nothing.
 */
const me = ref<Me | null>(null)
const loading = ref(true)

onMounted(async () => {
    // Read beside the account, not after it: it only fills the Working hours timezone when none are saved.
    const zoneRead = readEnterpriseZone()
    try {
        me.value = await api.me()
    } catch {
        me.value = null
    } finally {
        loading.value = false
    }
    showHours(me.value?.user.workingHours ?? null)
    // Not awaited: a browser choice being carried over to the account never holds the page up.
    void loadUiMode(me.value?.user.uiMode)
    await zoneRead
})

/**
 * #2006: the hours this person is emailed about Needs you in, one window across every enterprise of theirs. With none
 * saved the fields are empty and the timezone starts as the timezone of the enterprise this screen is open in. A
 * refused save says why and puts the fields back to what is saved, as the HQ timezone picker does.
 */
const savedHours = ref<WorkingHours | null>(null)
/** What the start and end fields hold: the kit Input hands its value back as typed. */
const hoursStart = ref<string | number>('')
const hoursEnd = ref<string | number>('')
const hoursZone = ref('')
const enterpriseZone = ref<string | null>(null)
const hoursError = ref<string | null>(null)
/** What the last Save or Clear did, said once it lands, as the team charter's Save says "Saved.". */
const hoursNotice = ref<string | null>(null)
const savingHours = ref(false)

/** The zones the picker offers: the chosen one and the enterprise's first, then every zone this browser knows. */
const hoursZoneChoices = computed(() => availableTimezones(hoursZone.value, enterpriseZone.value ?? undefined))

function browserZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/** The saved hours into the fields; with none, empty times and the enterprise's zone. */
function showHours(hours: WorkingHours | null): void {
    savedHours.value = hours
    hoursStart.value = hours?.start ?? ''
    hoursEnd.value = hours?.end ?? ''
    hoursZone.value = hours?.timezone ?? enterpriseZone.value ?? browserZone()
}

/** The enterprise's zone, the starting timezone for somebody with no hours saved. A refused read leaves the browser's. */
async function readEnterpriseZone(): Promise<void> {
    try {
        enterpriseZone.value = (await api.enterpriseTimezone()).timezone
    } catch {
        enterpriseZone.value = null
    }
    if (!savedHours.value && enterpriseZone.value) {
        hoursZone.value = enterpriseZone.value
    }
}

async function saveHours(): Promise<void> {
    // One save at a time: a second click while one is in flight sends nothing.
    if (savingHours.value) {
        return
    }
    hoursError.value = null
    hoursNotice.value = null
    savingHours.value = true
    // The kit Input hands what was typed up on the next tick.
    await nextTick()
    const hours: WorkingHours = {
        start: String(hoursStart.value),
        end: String(hoursEnd.value),
        timezone: hoursZone.value,
    }
    try {
        await api.saveWorkingHours(hours)
        showHours(hours)
        hoursNotice.value = 'Saved.'
    } catch (caught) {
        hoursError.value = caught instanceof Error ? caught.message : 'Your hours were not saved.'
        showHours(savedHours.value)
    } finally {
        savingHours.value = false
    }
}

async function clearHours(): Promise<void> {
    if (savingHours.value) {
        return
    }
    hoursError.value = null
    hoursNotice.value = null
    savingHours.value = true
    try {
        await api.clearWorkingHours()
        showHours(null)
        hoursNotice.value = "Cleared. Each enterprise's hours apply, or 8:00am to 8:00pm."
    } catch (caught) {
        hoursError.value = caught instanceof Error ? caught.message : 'Your hours were not cleared.'
    } finally {
        savingHours.value = false
    }
}

/** #1949: the person's Standard/Advanced mode, the same shared value every #1749 screen reads. */
const { mode } = useUiMode()
const advancedMode = computed(() => mode.value === 'advanced')
const savingMode = ref(false)
const modeNotSaved = ref(false)

/**
 * #2101: the mode in use, named in plain words. It follows the switch the moment it flips and goes back with it when
 * the save fails, because both read the same shared value.
 */
const modeName = computed(() => (advancedMode.value ? 'Advanced' : 'Standard'))

/** #2101: what the mode in use does to the Details under a reply in chat, in one sentence. */
const modeDetails = computed(() =>
    advancedMode.value
        ? 'The Details under each reply in chat are open, and you can still fold them.'
        : 'The Details under each reply in chat are folded behind a Details toggle. Open it to read them.',
)

/** Saves the flipped switch on the account. A failed save moves it back and says so in one line. */
async function changeMode(advanced: boolean): Promise<void> {
    modeNotSaved.value = false
    savingMode.value = true
    try {
        await saveUiMode(advanced ? 'advanced' : 'standard')
    } catch {
        modeNotSaved.value = true
    } finally {
        savingMode.value = false
    }
}

/** Owning is said out loud, never inferred from an empty list: the same convention the guard reads. */
const owner = computed(() => me.value?.isOwner === true)

/** An owner holds the whole enterprise with nothing enumerated, so has no chips to show. */
const chips = computed(() => (owner.value ? [] : scopeChips(me.value?.scopes)))

/** An API older than #630 sends no inviter at all, which reads the same as none being recorded. */
const inviter = computed(() => me.value?.invitedBy ?? null)

/**
 * An inviter is recorded but cannot be named (removed since, no longer a person, or the lookup failed). The
 * screen then says only that it cannot show them, and gives no reason it does not know.
 */
const inviterUnavailable = computed(() => !inviter.value && me.value?.inviterUnavailable === true)

/**
 * Who is signed in, by name where they have one and by GitHub login where they have one — never a blank. Somebody
 * who signed in without GitHub (#636) has no login to show.
 */
function signedInAs(user: Me['user']): string {
    if (!user.githubLogin) {
        return user.name || 'you'
    }
    return user.name ? `${user.name} (@${user.githubLogin})` : `@${user.githubLogin}`
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle kicker="Account">What you hold</ZhPageTitle>
		<p v-if="loading" class="text-muted-foreground text-sm">Loading…</p>
		<p v-else-if="!me" class="text-destructive text-sm" data-testid="account-error">
			Could not load your account. Reload the page to try again.
		</p>
		<template v-else>
			<p class="text-muted-foreground text-sm" data-testid="account-signed-in">
				Signed in as {{ signedInAs(me.user) }} to {{ me.enterprise.name }}.
			</p>

			<ZhPanel measure="prose" class="space-y-3" aria-labelledby="account-grant-heading">
				<ZhSectionHeading id="account-grant-heading">Your access</ZhSectionHeading>
				<div data-testid="account-ownership" :data-owner="owner ? 'true' : 'false'" class="space-y-2 text-sm">
					<template v-if="owner">
						<ZhSystemLabel as="span" tone="system">Owner</ZhSystemLabel>
						<p>
							You own {{ me.enterprise.name }}. Owning it is the whole grant, so there is no list of
							scopes beside it.
						</p>
					</template>
					<template v-else>
						<p>You are a member of {{ me.enterprise.name }}. You can reach what is listed here.</p>
						<span v-if="chips.length" class="flex flex-wrap gap-1">
							<ZhSystemLabel v-for="chip in chips" :key="chip.key" as="span" data-testid="scope-chip">
								{{ chip.label }}
							</ZhSystemLabel>
						</span>
						<p v-else class="text-muted-foreground">
							No grant: nothing in this enterprise has been opened to you yet beyond your own account.
						</p>
						<p class="text-muted-foreground">Only an owner can change what you hold.</p>
					</template>
				</div>
			</ZhPanel>

			<!-- #882: the person's own mailbox address in this enterprise, given by an owner on People. -->
			<ZhPanel measure="prose" class="space-y-3" aria-labelledby="account-mailbox-heading">
				<ZhSectionHeading id="account-mailbox-heading">Your mail address</ZhSectionHeading>
				<div data-testid="account-mailbox" class="space-y-2 text-sm">
					<template v-if="me.mailAddress">
						<p class="font-mono" data-testid="account-mail-address">{{ me.mailAddress }}</p>
						<p class="text-muted-foreground">
							Mail sent to it is kept for you in {{ me.enterprise.name }}, and for nobody else.
						</p>
						<!-- The mailbox itself has a page of its own (Inbox, in the nav), with every command on it. -->
						<p>
							<RouterLink to="/inbox" class="underline underline-offset-2" data-testid="account-inbox-link">
								Open your inbox
							</RouterLink>
						</p>
					</template>
					<p v-else class="text-muted-foreground" data-testid="account-no-mailbox">
						You have no mail address in {{ me.enterprise.name }} yet. An owner gives you one on People.
					</p>
				</div>
			</ZhPanel>

			<!-- #888: the address and steps for connecting an app to that mail, and the apps this person connected. -->
			<ConnectYourMailPanel v-if="me.mailAddress" :me="me" />
			<!-- #890: a mail-only token of the person's own, for a client that cannot sign in. -->
			<MyMailTokensPanel v-if="me.mailAddress" />

			<ZhPanel measure="prose" class="space-y-3" aria-labelledby="account-inviter-heading">
				<ZhSectionHeading id="account-inviter-heading">Who brought you in</ZhSectionHeading>
				<div data-testid="account-inviter" class="space-y-2 text-sm">
					<template v-if="inviter">
						<p class="flex items-center gap-2">
							<!-- Square, as every brand surface is: the kit draws no rounded lockup (BG §17.2). -->
							<img v-if="inviter.avatarUrl" :src="inviter.avatarUrl" alt="" class="size-8" />
							<span class="font-medium" data-testid="account-inviter-name">
								{{ inviter.name || inviter.githubLogin }}
							</span>
							<span v-if="inviter.name" class="text-muted-foreground">@{{ inviter.githubLogin }}</span>
						</p>
						<p class="text-muted-foreground">They invited you. Ask them if you need more than you hold.</p>
					</template>
					<p v-else-if="inviterUnavailable" data-testid="account-inviter-unavailable">
						We can't show who invited you.
						<template v-if="owner">
							You own {{ me.enterprise.name }}, so what it opens to anybody is yours to decide.
						</template>
						<template v-else>
							Ask an owner of {{ me.enterprise.name }} if you need more than you hold.
						</template>
					</p>
					<p v-else-if="owner">
						Nobody is recorded as having invited you: you own {{ me.enterprise.name }}, so what it opens
						to anybody is yours to decide.
					</p>
					<p v-else>
						Nobody is recorded as having invited you — you joined before invitations kept a record of who
						sent them. Ask an owner of {{ me.enterprise.name }} if you need more than you hold.
					</p>
				</div>
			</ZhPanel>

			<ZhPanel measure="prose" class="space-y-3" aria-labelledby="account-mode-heading">
				<ZhSectionHeading id="account-mode-heading">How much detail you see</ZhSectionHeading>
				<div class="flex items-start justify-between gap-4 text-sm">
					<div class="space-y-1">
						<!-- The words are part of the control: clicking the label flips the switch too. -->
						<label for="account-ui-mode" class="font-medium">Advanced mode</label>
						<p id="account-ui-mode-explainer" class="text-muted-foreground">
							Shows IDs, tool names and error codes. Standard shows plain words only.
							In Standard, the Details under a reply in chat are folded behind a Details toggle; in
							Advanced they are open.
						</p>
					</div>
					<Switch
						id="account-ui-mode"
						:model-value="advancedMode"
						aria-label="Advanced mode"
						aria-describedby="account-ui-mode-explainer"
						:disabled="savingMode"
						data-testid="account-ui-mode"
						@update:model-value="changeMode"
					/>
				</div>
				<!-- #2101: says which mode is on, so the switch never has to be read to know it. -->
				<div class="space-y-1 text-sm" aria-live="polite" data-testid="account-ui-mode-current">
					<p class="flex items-center gap-2">
						<span class="text-muted-foreground">On now:</span>
						<ZhSystemLabel as="span" tone="system">{{ modeName }}</ZhSystemLabel>
					</p>
					<p class="text-muted-foreground">{{ modeDetails }}</p>
				</div>
				<p v-if="modeNotSaved" class="text-destructive text-sm" role="alert" data-testid="account-ui-mode-error">
					Your choice was not saved. Try again.
				</p>
			</ZhPanel>

			<ZhPanel
				measure="prose"
				class="space-y-3"
				aria-labelledby="account-working-hours-heading"
				data-testid="working-hours-section"
			>
				<ZhSectionHeading id="account-working-hours-heading">Working hours</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					We send the Needs you email only inside these hours. With none set, each enterprise's hours apply,
					or 8:00am to 8:00pm.
				</p>
				<p class="text-muted-foreground text-sm" data-testid="working-hours-how">
					Your own hours apply every day and replace each enterprise's hours. Anything that arrives outside them
					waits and comes in your next email. An end earlier than the start runs past midnight.
				</p>
				<p v-if="hoursError" class="text-destructive text-sm" role="alert" data-testid="working-hours-error">
					{{ hoursError }}
				</p>
				<p
					v-else-if="hoursNotice"
					class="text-muted-foreground text-sm"
					role="status"
					data-testid="working-hours-notice"
				>
					{{ hoursNotice }}
				</p>
				<form class="flex flex-wrap items-end gap-2 text-sm" @submit.prevent="saveHours">
					<div class="space-y-1">
						<Label for="working-hours-start">Start</Label>
						<Input
							id="working-hours-start"
							v-model="hoursStart"
							data-testid="working-hours-start"
							type="time"
							class="w-32"
						/>
					</div>
					<div class="space-y-1">
						<Label for="working-hours-end">End</Label>
						<Input id="working-hours-end" v-model="hoursEnd" data-testid="working-hours-end" type="time" class="w-32" />
					</div>
					<div class="min-w-56 flex-1 space-y-1">
						<Label for="working-hours-timezone">Timezone</Label>
						<Select v-model="hoursZone" data-testid="working-hours-timezone" aria-label="Working hours timezone">
							<SelectTrigger id="working-hours-timezone" class="w-full" aria-label="Working hours timezone">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="zone in hoursZoneChoices" :key="zone" :value="zone">{{ zone }}</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<Button
						type="button"
						size="sm"
						variant="outline"
						:disabled="savingHours"
						data-testid="working-hours-save"
						@click="saveHours"
					>
						Save
					</Button>
					<Button
						type="button"
						size="sm"
						variant="ghost"
						:disabled="savingHours || !savedHours"
						data-testid="working-hours-clear"
						@click="clearHours"
					>
						Clear
					</Button>
				</form>
			</ZhPanel>

			<SignInWaysPanel />
		</template>
	</section>
</template>
